<template>
  <div id="menu-list">
    <Breadcrumbs
      :crumbs="[
        { name: globals.info.school_name, link: `${pathPrefix}/` },
        { name: sectionName, link: `${pathPrefix}/dining` },
      ]"
    />
    <LoadingIndicator v-show="isLoading" />
    <div
      v-show="!isLoading"
      ref="container"
      class="page-container"
    >
      <div
        v-if="dynamicPageFiltersStore.getDiningPageFilters && dynamicPageFiltersStore.getDiningPageFilters.length > 1"
        class="filter"
      >
        <select
          v-model="selectedFilter"
          name="filter"
          :aria-label="$t('pages.dining.diningFilter')"
          data-testid="dining-filter-dropdown"
          @change="filterCallback"
        >
          <option
            v-for="filter in dynamicPageFiltersStore.getDiningPageFilters"
            :key="filter.id"
            :data-testid="`dining-filter-option-${filter.id}`"
            :value="filter.id"
          >
            {{ filter.name.toUpperCase() }}
          </option>
        </select>
      </div>
      <menu-item
        v-for="item in menus"
        :key="item.id"
        data-testid="dining-menu-item"
        :item="item"
      />
    </div>
    <div class="dining-legal page-container">
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="page-container-item"
        v-html="diningDisclaimer.fda_statement"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { onBeforeRouteUpdate } from 'vue-router';
import isEmpty from 'lodash/isEmpty';
import concat from 'lodash/concat';
import { useScroll, useThrottleFn } from '@vueuse/core';

import Breadcrumbs from '../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../components/global/LoadingIndicator.vue';
import MenuItem from '../../components/pages/dining/menu-item';
import { usePageHelpers } from '../../composables/usePageHelpers';
import getLocale from '../../helpers/locale';
import { fetchFiltersAndDisclaimer, fetchMenus } from '../../helpers/section/dining.async';
import { useDynamicPageFiltersStore } from '@/stores/dynamicPageFiltersStore';
import { useMainStore } from '@/stores/mainStore';

defineOptions({ name: 'Menus' });

const mainStore = useMainStore();
const dynamicPageFiltersStore = useDynamicPageFiltersStore();
const route = useRoute();
const { $axios } = useNuxtApp();

const { data } = await useAsyncData('dining', () => fetchFiltersAndDisclaimer({ pageQuery: route.query, api: $axios }));

const selectedFilter = ref(data.value?.selectedFilter ?? null);
const { filterCallback } = usePageHelpers({ selectedFilter });

const menus = ref(data.value?.menus ?? []);
const links = ref(data.value?.links ?? null);

const isLoading = ref(false);
const isLoadingAdditionalMenu = ref(false);
const container = ref(null);

let stopInfiniteScrollWatch = null;

const sectionName = computed(() => mainStore.sectionName);
const globals = computed(() => mainStore.globals);
const pathPrefix = computed(() => mainStore.pathPrefix);
const diningDisclaimer = computed(() => mainStore.diningDisclaimer);
const hasMoreMenus = computed(() => Boolean(menus.value.length && links.value?.next));

useHead({
  title: () => sectionName.value,
});

async function fetchFiltersAndMenus(query, cookie) {
  isLoading.value = true;
  try {
    const langCode = getLocale(cookie);
    if (!isEmpty(langCode) && langCode !== 'en') {
      const result = await fetchFiltersAndDisclaimer({
        pageQuery: query,
        api: $axios,
      });
      selectedFilter.value = result.selectedFilter;
      menus.value = result.menus;
      links.value = result.links;
      isLoading.value = false;
      return;
    }
    if (!isEmpty(query)) {
      const result = await fetchMenus({
        pageQuery: query,
        api: $axios,
      });
      selectedFilter.value = result.selectedFilter;
      menus.value = result.menus;
      links.value = result.links;
      isLoading.value = false;
    }
  } catch (error) {
    console.error('Error in fetchFiltersAndMenus:', error);
  } finally {
    isLoading.value = false;
  }
}

async function addMenuResults() {
  if (!links.value?.next) return;
  try {
    if (isLoadingAdditionalMenu.value) return;
    isLoadingAdditionalMenu.value = true;
    const url = new URL(links.value.next);
    const newPath = url.pathname.concat(url.search);
    const { data: responseData } = await $axios.get(newPath);
    const nextMenuPage = responseData.menus;
    links.value = responseData.meta.links;
    menus.value = concat(menus.value, nextMenuPage);
  } catch (e) {
    console.error('Error loading additional menu results:', {
      message: e.message,
      status: e.response?.status,
      statusText: e.response?.statusText,
    });
  } finally {
    isLoadingAdditionalMenu.value = false;
  }
}

function setupInfiniteScroll() {
  const SCROLL_THRESHOLD = 100;
  const { y } = useScroll(window);

  const checkScrollPosition = useThrottleFn(() => {
    if (!hasMoreMenus.value || isLoadingAdditionalMenu.value) {
      return;
    }

    const el = container.value;
    if (!el) return;
    const containerBottom = el.getBoundingClientRect().bottom;
    const viewportHeight = window.innerHeight;
    const distanceFromBottom = containerBottom - viewportHeight;

    if (distanceFromBottom < SCROLL_THRESHOLD) {
      addMenuResults();
    }
  }, 100);

  stopInfiniteScrollWatch = watch(y, checkScrollPosition, { immediate: true });
}

onMounted(() => {
  setupInfiniteScroll();
});

onBeforeUnmount(() => {
  if (stopInfiniteScrollWatch) {
    stopInfiniteScrollWatch();
  }
});

onBeforeRouteUpdate((to, from, next) => {
  if (import.meta.client && to.query.filter !== from.query.filter) {
    fetchFiltersAndMenus(to.query, document.cookie);
  }
  next();
});
</script>

<style scoped lang="scss">
#menu-list {
  .menu-filter {
    float: right;
  }
  .menus {
    max-width: 100%;
    max-height: 300px;
    overflow: hidden;
    border-bottom: 1px solid #ddd;
  }
  .page-container {
    padding-bottom: 0;
  }
  .dining-legal {
    line-height: 17px;
    color: #555;
    padding-bottom: 30px;
    .page-container-item {
      border-bottom: none;
    }
    :deep() {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin-bottom: 15px;
        font-weight: 700;
        line-height: 1;
        color: var(--heading-color);
      }
      a {
        color: var(--links-color);
      }
      p {
        margin-bottom: 15px;
        font-weight: 400;
        font-size: 13px;
      }
      li,
      blockquote,
      pre {
        font-size: 13px;
      }
    }
  }
}
</style>
